import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { Button, Card, Stepper, useSnackbar } from '@tecsinapse/cortex-react'
import React, { ReactElement, useCallback } from 'react'
import { BsListCheck } from 'react-icons/bs'
import { filterNull } from '@/core/utils/array'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Loading } from '../../core/components/loading'

interface FormWizardProps {
  initialValues: any
  submitHandler: any
  schemaArray: any
  children: any
  activeStep: number
  setActiveStep: React.Dispatch<React.SetStateAction<number>>
  onClose: any
  canInitialize?: boolean
}

export const FormWizard = ({
  initialValues,
  submitHandler,
  schemaArray,
  children,
  activeStep,
  setActiveStep,
  onClose,
  canInitialize
}: FormWizardProps) => {
  const { snackbar } = useSnackbar()

  const getResolverByActiveStep = useCallback(
    () => (activeStep > 2 ? undefined : yupResolver(schemaArray[activeStep])),
    [activeStep, schemaArray]
  )

  const form = useForm({
    values: initialValues,
    resolver: getResolverByActiveStep(),
    mode: 'all',
    reValidateMode: 'onBlur',
    resetOptions: { keepDirtyValues: true }
  })

  const { handleSubmit } = form

  const handlePartialSubmit = async (values: any) => {
    const steps = filterNull<ReactElement>(children.props.children)

    const isLastPage = activeStep === steps.length - 1
    const activeStepProps = steps[activeStep].props
    const { partialSubmit } = activeStepProps
    const shouldSubmit = isLastPage || partialSubmit

    if (shouldSubmit) {
      try {
        await submitHandler(values)
      } catch (error) {
        snackbar.show('error', t`Confira os dados!`)
      }
    }

    if (!isLastPage) {
      setActiveStep((a: number) => a + 1)
    }

    return true
  }

  const onError = (errors: any) => {
    if (errors.produtos) {
      Object.entries(errors.produtos).forEach(([, error]: [string, any]) => {
        if (error.idsTiposAcesso?.message) {
          snackbar.show('error', error.idsTiposAcesso.message)
        }
      })
      return
    }
    if (errors.cargos?.message) {
      snackbar.show('error', errors.cargos.message)
      return
    }
    snackbar.show('error', t`Confira os dados obrigatórios!`)
  }

  const formSteps = filterNull<ReactElement>(children.props.children)
  const stepProps = filterNull<ReactElement>(children.props.children)
  const title = stepProps[activeStep].props?.children?.props?.title

  if (!canInitialize) return <Loading />

  return (
    <>
      <Card className={'mb-deca'}>
        <div className={'flex flex-row items-center gap-x-deca'}>
          <BsListCheck className={'text-primary-medium'} />
          <p className={'font-bold'}>
            <Trans>{title}</Trans>
          </p>
        </div>
        <Stepper.Root segmented interactive={false}>
          {filterNull(children.props.children).map(
            (_: unknown, index: number) => (
              <Stepper.Node
                key={index}
                marked={activeStep >= index}
                intent={activeStep === index ? 'warning' : 'success'}
                className={'bg-transparent hover:cursor-default w-full'}
              />
            )
          )}
        </Stepper.Root>
      </Card>
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmit(data => handlePartialSubmit(data), onError)}
        >
          <div className={'space-y-deca mb-deca'}>
            {formSteps[activeStep]}
            <div
              className={
                'flex flex-row items-start justify-between space-x-deca'
              }
            >
              <Button
                type={'button'}
                variants={{
                  variant: 'outline',
                  className: 'md:px-[10rem]'
                }}
                onClick={() => {
                  setActiveStep((current: number) => {
                    if (current === 0) {
                      onClose?.()
                      return current
                    }
                    return current - 1
                  })
                }}
              >
                {activeStep !== 0 ? (
                  <Trans>Voltar</Trans>
                ) : (
                  <Trans>Cancelar</Trans>
                )}
              </Button>
              <Button variants={{ className: 'md:px-[10rem]' }} type={'submit'}>
                {activeStep !==
                filterNull(children.props.children).length - 1 ? (
                  <Trans>Avançar</Trans>
                ) : (
                  <Trans>Confirmar</Trans>
                )}
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </>
  )
}
